import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useClickOutside, useDebounce } from '@aimsos/hooks';
import UiAvatar from '../Avatar.jsx';
import Badge from '../Badge.jsx';
import SearchInput from '../SearchInput.jsx';
import Skeleton from '../Skeleton.jsx';
import {
  resolveOptionAvatarClassName,
  resolveOptionLabelText,
} from '../SearchableSelect/searchableSelect.utils.js';
import {
  filterAutocompleteOptions,
  resolveAutocompleteOptionLabel,
  resolveAutocompleteOptionSubtitle,
} from './searchAutocomplete.utils.js';
import { Search, X } from 'lucide-react';

const defaultGetOptionId = (option) => option.id;
const defaultGetOptionLabel = (option) => option.name;
const defaultGetOptionSubtitle = (option) => option.subtitle;

const OptionAvatar = memo(function SearchAutocompleteOptionAvatar({ option, presetIndex }) {
  const initials = resolveOptionLabelText(option);
  const hasImage = typeof option.avatarSrc === 'string' && option.avatarSrc.trim().length > 0;
  const bgClass = hasImage ? '' : resolveOptionAvatarClassName(option, presetIndex);

  return (
    <UiAvatar
      src={hasImage ? option.avatarSrc : undefined}
      alt={option.name ?? 'Option'}
      initials={initials}
      size="lg"
      className={[
        '!border-transparent',
        hasImage ? '' : bgClass,
        'text-white [&>span]:font-bold [&>span]:leading-none [&>span]:text-white',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
});

const OptionRow = memo(function SearchAutocompleteOptionRow({
  option,
  id,
  presetIndex,
  isSelected,
  isHighlighted,
  showAvatar,
  getOptionLabel,
  getOptionSubtitle,
  onSelect,
}) {
  const label = resolveAutocompleteOptionLabel(option, getOptionLabel);
  const subtitle = resolveAutocompleteOptionSubtitle(option, getOptionSubtitle);

  const handleMouseDown = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleClick = useCallback(() => {
    onSelect(option);
  }, [onSelect, option]);

  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={[
        'w-full rounded-md px-3 py-2 text-left transition-colors',
        isSelected
          ? 'bg-violet-300 hover:bg-violet-400 dark:bg-violet-900/65 dark:hover:bg-violet-900/80'
          : isHighlighted
            ? 'bg-violet-200/90 hover:bg-violet-300 dark:bg-violet-900/45 dark:hover:bg-violet-900/60'
            : 'hover:bg-violet-100/85 dark:hover:bg-violet-900/35 focus-visible:bg-violet-100/85 dark:focus-visible:bg-violet-900/35',
        isSelected ? 'text-content font-medium' : 'text-content',
      ].join(' ')}
    >
      <div className="flex min-w-0 items-center gap-3">
        {showAvatar ? <OptionAvatar option={option} presetIndex={presetIndex} /> : null}
        <div className="min-w-0 flex-1">
          <p id={id} className="truncate text-sm">
            {label}
          </p>
          {subtitle ? <p className="truncate text-xs text-content-muted">{subtitle}</p> : null}
        </div>
      </div>
    </button>
  );
});

const SelectedBadge = memo(function SearchAutocompleteSelectedBadge({
  option,
  onRemove,
  getOptionId,
  getOptionLabel,
}) {
  const label = resolveAutocompleteOptionLabel(option, getOptionLabel);
  const optionId = getOptionId(option);

  return (
    <Badge
      id={optionId}
      variant="purple"
      size="small"
      closable
      onClose={onRemove}
      closeButtonAriaLabel={`Remove ${label}`}
      className="!rounded-full max-w-[16rem]"
    >
      <span className="truncate max-w-[14rem]">{label}</span>
    </Badge>
  );
});

/**
 * @typedef {object} SearchAutocompleteOption
 * @property {string} id
 * @property {string} name
 * @property {string} [subtitle]
 * @property {string} [initial]
 * @property {string} [avatarSrc]
 * @property {string} [avatarClassName]
 */

/**
 * Input de búsqueda con dropdown integrado y filtrado local (typeahead/autocomplete).
 *
 * @param {SearchAutocompleteOption[]} [options]
 * @param {string | null} [selectedValue]
 * @param {(option: SearchAutocompleteOption) => void} [onSelect]
 * @param {(option: SearchAutocompleteOption) => string} [getOptionId]
 * @param {(option: SearchAutocompleteOption) => string} [getOptionLabel]
 * @param {(option: SearchAutocompleteOption) => string} [getOptionSubtitle]
 * @param {(query: string) => void} [onChange] - callback con query debounced
 * @param {(query: string) => void} [onInputValueChange] - callback inmediato al tipear
 * @param {boolean} [showAvatar] - usa avatar con mismo fallback visual de SearchableSelect
 * @param {boolean} [isMulti] - habilita selección múltiple
 * @param {string[]} [selectedValues] - ids seleccionados en modo múltiple (controlado)
 * @param {string[]} [defaultSelectedValues] - ids iniciales en modo múltiple (no controlado)
 * @param {(nextValues: string[]) => void} [onSelectedValuesChange] - callback de cambios en modo múltiple
 */
const SearchAutocomplete = ({
  options = [],
  selectedValue = null,
  selectedValues,
  defaultSelectedValues = [],
  onSelect,
  onSelectedValuesChange,
  getOptionId = defaultGetOptionId,
  getOptionLabel = defaultGetOptionLabel,
  getOptionSubtitle = defaultGetOptionSubtitle,
  onChange,
  onInputValueChange,
  debounceMs = 300,
  placeholder = 'Search...',
  emptyMessage = 'No results',
  minQueryLength = 0,
  isLoading = false,
  loadingRows = 5,
  loadingAnimationStyle = 'shimmer',
  showAvatar = true,
  isMulti = false,
  disabled = false,
  className = '',
  dropdownClassName = '',
  dropdownMaxHeight = '16rem',
  openOnFocus = true,
  closeOnSelect = true,
  setInputOnSelect = true,
  inputValue,
  defaultInputValue = '',
}) => {
  const [internalInputValue, setInternalInputValue] = useState(defaultInputValue);
  const [internalSelectedValues, setInternalSelectedValues] = useState(defaultSelectedValues);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasWrappedBadges, setHasWrappedBadges] = useState(false);
  const inputId = useId();
  const listId = `${inputId}-listbox`;
  const wrapperRef = useRef(null);
  const multiInputRef = useRef(null);
  const multiContentRef = useRef(null);
  const lastEmittedQueryRef = useRef(null);
  const rafMeasureRef = useRef(0);

  const onSelectRef = useRef(onSelect);
  const onSelectedValuesChangeRef = useRef(onSelectedValuesChange);
  const onChangeRef = useRef(onChange);
  const onInputValueChangeRef = useRef(onInputValueChange);
  const getOptionIdRef = useRef(getOptionId);
  const getOptionLabelRef = useRef(getOptionLabel);
  const isMultiRef = useRef(isMulti);
  const isControlledRef = useRef(false);
  const isSelectedValuesControlledRef = useRef(false);
  const setInputOnSelectRef = useRef(setInputOnSelect);
  const closeOnSelectRef = useRef(closeOnSelect);
  const multiSelectedValuesRef = useRef([]);
  const filteredOptionsRef = useRef([]);
  const highlightedIndexRef = useRef(-1);
  const showPanelRef = useRef(false);
  const disabledRef = useRef(disabled);
  const openOnFocusRef = useRef(openOnFocus);
  const selectOptionImplRef = useRef(() => {});
  const removeSelectedImplRef = useRef(() => {});

  onSelectRef.current = onSelect;
  onSelectedValuesChangeRef.current = onSelectedValuesChange;
  onChangeRef.current = onChange;
  onInputValueChangeRef.current = onInputValueChange;
  getOptionIdRef.current = getOptionId;
  getOptionLabelRef.current = getOptionLabel;

  const isControlled = typeof inputValue === 'string';
  const query = isControlled ? inputValue : internalInputValue;
  const debouncedQuery = useDebounce(query, debounceMs);

  const matchingOptions = useMemo(
    () => filterAutocompleteOptions(options, debouncedQuery, getOptionId, getOptionLabel, getOptionSubtitle),
    [options, debouncedQuery, getOptionId, getOptionLabel, getOptionSubtitle],
  );
  filteredOptionsRef.current = matchingOptions;
  const matchingOptionCount = matchingOptions.length;

  const isSelectedValuesControlled = Array.isArray(selectedValues);
  const multiSelectedValues = isMulti
    ? isSelectedValuesControlled
      ? selectedValues
      : internalSelectedValues
    : [];

  isControlledRef.current = isControlled;
  isMultiRef.current = isMulti;
  isSelectedValuesControlledRef.current = isSelectedValuesControlled;
  setInputOnSelectRef.current = setInputOnSelect;
  closeOnSelectRef.current = closeOnSelect;
  multiSelectedValuesRef.current = multiSelectedValues;
  highlightedIndexRef.current = highlightedIndex;
  disabledRef.current = disabled;
  openOnFocusRef.current = openOnFocus;

  /** Index para avatar presets + option por id (un solo pase sobre options). */
  const optionMetaById = useMemo(() => {
    const map = new Map();
    options.forEach((option, index) => {
      map.set(getOptionId(option), { option, presetIndex: index });
    });
    return map;
  }, [options, getOptionId]);

  const selectedValueSet = useMemo(() => new Set(multiSelectedValues), [multiSelectedValues]);

  const selectedOptions = useMemo(() => {
    const out = [];
    for (const id of multiSelectedValues) {
      const meta = optionMetaById.get(id);
      if (meta) out.push(meta.option);
    }
    return out;
  }, [multiSelectedValues, optionMetaById]);

  const meetsMinQueryLength = query.trim().length >= Math.max(0, minQueryLength);
  const showPanel = isOpen && !disabled && meetsMinQueryLength;
  showPanelRef.current = showPanel;

  useEffect(() => {
    if (lastEmittedQueryRef.current === null) {
      lastEmittedQueryRef.current = debouncedQuery;
      return;
    }
    if (lastEmittedQueryRef.current === debouncedQuery) return;
    lastEmittedQueryRef.current = debouncedQuery;
    onChangeRef.current?.(debouncedQuery);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!showPanel || matchingOptionCount === 0) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex((current) => {
      if (current < 0) return -1;
      if (current >= matchingOptionCount) return matchingOptionCount - 1;
      return current;
    });
  }, [showPanel, matchingOptionCount]);

  const handleClosePanel = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setIsInputFocused(false);
  }, []);

  useClickOutside(wrapperRef, handleClosePanel);

  const setQueryValue = useCallback(
    (nextValue) => {
      if (!isControlledRef.current) {
        setInternalInputValue(nextValue);
      }
      onInputValueChangeRef.current?.(nextValue);
    },
    [],
  );

  selectOptionImplRef.current = (option) => {
    onSelectRef.current?.(option);

    if (isMultiRef.current) {
      const id = getOptionIdRef.current(option);
      const current = multiSelectedValuesRef.current;
      const has = current.includes(id);
      const nextValues = has ? current.filter((x) => x !== id) : [...current, id];

      if (!isSelectedValuesControlledRef.current) {
        setInternalSelectedValues(nextValues);
      }
      onSelectedValuesChangeRef.current?.(nextValues);
      setQueryValue('');
      return;
    }

    if (setInputOnSelectRef.current) {
      const nextQuery = resolveAutocompleteOptionLabel(option, getOptionLabelRef.current);
      setQueryValue(nextQuery);
    }

    if (closeOnSelectRef.current) {
      setIsOpen(false);
      setHighlightedIndex(-1);
      setIsInputFocused(false);
    }
  };

  const stableSelectOption = useCallback((option) => {
    selectOptionImplRef.current(option);
  }, []);

  removeSelectedImplRef.current = (optionId) => {
    if (!isMultiRef.current) return;
    const current = multiSelectedValuesRef.current;
    if (!current.includes(optionId)) return;
    const nextValues = current.filter((id) => id !== optionId);
    if (!isSelectedValuesControlledRef.current) {
      setInternalSelectedValues(nextValues);
    }
    onSelectedValuesChangeRef.current?.(nextValues);
  };

  const stableRemoveSelected = useCallback((optionId) => {
    removeSelectedImplRef.current(optionId);
  }, []);

  const handleInputChange = useCallback(
    (event) => {
      setQueryValue(event.target.value);
      if (!disabledRef.current) {
        setIsOpen(true);
      }
    },
    [setQueryValue],
  );

  const handleInputFocus = useCallback(() => {
    if (!disabledRef.current && openOnFocusRef.current) {
      setIsOpen(true);
    }
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  useEffect(() => {
    if (!isMulti) {
      setHasWrappedBadges(false);
      return undefined;
    }

    const element = multiContentRef.current;
    if (!element) return undefined;

    const measureWrap = () => {
      const children = Array.from(element.children);
      if (children.length <= 1) {
        setHasWrappedBadges((prev) => (prev === false ? prev : false));
        return;
      }

      const firstTop = children[0]?.offsetTop ?? 0;
      const wrapped = children.some((child) => child.offsetTop > firstTop);
      setHasWrappedBadges((prev) => (prev === wrapped ? prev : wrapped));
    };

    const scheduleMeasure = () => {
      if (rafMeasureRef.current) {
        cancelAnimationFrame(rafMeasureRef.current);
      }
      rafMeasureRef.current = requestAnimationFrame(() => {
        rafMeasureRef.current = 0;
        measureWrap();
      });
    };

    scheduleMeasure();

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(element);
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (rafMeasureRef.current) {
        cancelAnimationFrame(rafMeasureRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [isMulti, selectedOptions.length, query]);

  const handleClear = useCallback(() => {
    setQueryValue('');
    if (!disabledRef.current && openOnFocusRef.current) {
      setIsOpen(true);
    }
    multiInputRef.current?.focus();
  }, [setQueryValue]);

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        handleClosePanel();
        return;
      }

      const panelOpen = showPanelRef.current;
      const list = filteredOptionsRef.current;
      const dis = disabledRef.current;

      if (!panelOpen || list.length === 0) {
        if (event.key === 'ArrowDown' && !dis) {
          event.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((current) => Math.min(current + 1, list.length - 1));
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((current) => Math.max(current - 1, -1));
        return;
      }

      if (event.key === 'Enter') {
        const idx = highlightedIndexRef.current;
        if (idx >= 0 && list[idx]) {
          event.preventDefault();
          selectOptionImplRef.current(list[idx]);
        }
      }
    },
    [handleClosePanel],
  );

  const listHasMaxHeight =
    dropdownMaxHeight != null &&
    dropdownMaxHeight !== 'none' &&
    String(dropdownMaxHeight).trim() !== '';

  const listStyle = listHasMaxHeight
    ? { ['--ss-autocomplete-max-height']: dropdownMaxHeight }
    : undefined;

  return (
    <div ref={wrapperRef} className={['relative w-full', className].filter(Boolean).join(' ')}>
      {isMulti ? (
        <div
          className={[
            'relative w-full rounded-md border border-edge-strong bg-surface px-3 text-sm text-content',
            'transition-colors duration-150',
            'hover:border-accent/50',
            hasWrappedBadges ? 'min-h-[38px] py-1' : 'h-[38px] py-0',
            isInputFocused ? 'border-accent/50 ring-2 ring-accent/50' : '',
            disabled ? 'opacity-60 cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseDown={(event) => {
            if (disabled) return;
            if (event.target instanceof HTMLElement && event.target.closest('button')) return;
            event.preventDefault();
            multiInputRef.current?.focus();
          }}
        >
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted">
            <Search size={16} />
          </span>

          <div
            ref={multiContentRef}
            className={[
              'ml-6 mr-6 flex flex-wrap gap-1.5',
              hasWrappedBadges
                ? 'min-h-6 items-start content-start py-1'
                : 'h-full items-center content-center',
            ].join(' ')}
          >
            {selectedOptions.map((option) => (
              <SelectedBadge
                key={`selected-badge-${getOptionId(option)}`}
                option={option}
                onRemove={stableRemoveSelected}
                getOptionId={getOptionId}
                getOptionLabel={getOptionLabel}
              />
            ))}

            <input
              ref={multiInputRef}
              type="search"
              value={query}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder={selectedOptions.length === 0 ? placeholder : ''}
              disabled={disabled}
              autoComplete="off"
              role="combobox"
              aria-expanded={showPanel}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0 && showPanel && matchingOptions[highlightedIndex]
                  ? `${listId}-item-${getOptionId(matchingOptions[highlightedIndex])}`
                  : undefined
              }
              className="min-w-[8ch] flex-1 self-center bg-transparent text-sm text-content placeholder:text-content-faint focus:outline-none [&::-webkit-search-cancel-button]:hidden"
            />
          </div>

          {!disabled && query ? (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded text-content-muted transition-colors hover:text-content"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      ) : (
        <SearchInput
          value={query}
          onChange={handleInputChange}
          onClear={disabled ? undefined : handleClear}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedIndex >= 0 && showPanel && matchingOptions[highlightedIndex]
              ? `${listId}-item-${getOptionId(matchingOptions[highlightedIndex])}`
              : undefined
          }
          className="[&>input]:h-[38px] [&>input]:py-0"
        />
      )}

      {showPanel ? (
        <div
          id={listId}
          role="listbox"
          aria-multiselectable={isMulti || undefined}
          className={[
            'absolute z-30 mt-1 w-full rounded-md border border-edge bg-surface-elevated p-0 shadow-lg',
            'dark:shadow-[0px_4px_12px_rgba(0,0,0,0.45)] overflow-hidden',
            dropdownClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              'overflow-y-auto scrollbar-aimsos-auto-sm',
              listHasMaxHeight
                ? 'max-h-[var(--ss-autocomplete-max-height)]'
                : 'max-h-none',
            ].join(' ')}
            style={listStyle}
          >
            {isLoading ? (
              <div className="space-y-2 px-3 py-3" aria-live="polite" aria-busy="true">
                {Array.from({ length: Math.max(1, loadingRows) }).map((_, index) => (
                  <div
                    key={`search-autocomplete-loading-${index}`}
                    className="flex items-center gap-3 rounded-md px-2 py-1.5"
                  >
                    {showAvatar ? (
                      <Skeleton
                        width={40}
                        height={40}
                        borderRadius="9999px"
                        animationStyle={loadingAnimationStyle}
                      />
                    ) : null}
                    <div className="flex-1 space-y-2">
                      <Skeleton
                        width={showAvatar ? '55%' : '65%'}
                        height={12}
                        borderRadius={6}
                        animationStyle={loadingAnimationStyle}
                      />
                      <Skeleton
                        width={showAvatar ? '80%' : '90%'}
                        height={10}
                        borderRadius={6}
                        animationStyle={loadingAnimationStyle}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : matchingOptions.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-content-muted">{emptyMessage}</p>
            ) : (
              <div className="flex flex-col gap-1 p-2">
                {matchingOptions.map((option, index) => {
                  const optionId = getOptionId(option);
                  const itemId = `${listId}-item-${optionId}`;
                  const meta = optionMetaById.get(optionId);
                  const presetIndex =
                    meta && Number.isInteger(meta.presetIndex) && meta.presetIndex >= 0
                      ? meta.presetIndex
                      : 0;
                  return (
                    <OptionRow
                      key={optionId}
                      id={itemId}
                      option={option}
                      presetIndex={presetIndex}
                      isSelected={isMulti ? selectedValueSet.has(optionId) : selectedValue === optionId}
                      isHighlighted={highlightedIndex === index}
                      showAvatar={showAvatar}
                      getOptionLabel={getOptionLabel}
                      getOptionSubtitle={getOptionSubtitle}
                      onSelect={stableSelectOption}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SearchAutocomplete;
